import api from './api';
import { ApiResponse } from '../types';

export interface ConclusionDetail {
  conclusionTitle: string;
  level: string;
  count: number;
  percentage: number;
}

export interface ConclusionUser {
  answerId: number;
  userName: string;
  studentNumber: string;
  schoolName: string;
  className: string;
  grade: string;
  score: number;
  indicatorName?: string;
  conclusionTitle?: string;
  conclusionLevel?: string;
}

export interface AnswerDetail {
  questionId: number;
  questionTitle: string;
  questionType: string;
  answerText: string;
  /** 题目原始 settings JSON（MATRIX schema-aware 渲染用） */
  questionSettings?: string | null;
  /** 用户答案原始 JSON（前端按题型解析） */
  answerRaw?: string | null;
  score: number | null;
}

export interface IndicatorResult {
  indicatorId: number;
  indicatorName: string;
  score: number;
  conclusionTitle: string | null;
  conclusionDescription: string | null;
  level: string | null;
}

export interface QuestionnaireAnswer {
  id: number;
  userId: number;
  userName: string;
  studentNumber: string;
  schoolName: string;
  className: string;
  grade: string;
  submittedAt: string;
  answerDetails: AnswerDetail[];
  indicatorResults: IndicatorResult[];
}

export interface IndicatorStatistics {
  indicatorId: number;
  indicatorName: string;
  totalAnswers: number;
  avgScore: number;
  minScore: number;
  maxScore: number;
  conclusions: ConclusionDetail[];
}

export interface ClassStatistics {
  classId: string;
  className: string;
  averageScore: number;
  studentCount: number;
}

export interface GradeStatistics {
  grade: string;
  averageScore: number;
  studentCount: number;
}

export interface SchoolOption {
  id: number;
  name: string;
}

export interface QuestionnaireStatisticsOverview {
  questionnaireId: number;
  questionnaireTitle: string;
  totalAnswers: number;
  lastCalculatedAt: string | null;
  calculationStatus: string;
}

export const statisticsService = {
  /**
   * Get questionnaires overview (optionally filtered by school)
   */
  getQuestionnairesOverview: async (schoolId?: number): Promise<QuestionnaireStatisticsOverview[]> => {
    const response = await api.get<ApiResponse<QuestionnaireStatisticsOverview[]>>(
      '/admin/statistics/questionnaires',
      { params: schoolId ? { schoolId } : {} }
    );
    return response.data.data;
  },

  /**
   * Get overall statistics for an indicator (optionally filtered by school)
   */
  getIndicatorStatistics: async (questionnaireId: number, indicatorId: number, schoolId?: number): Promise<IndicatorStatistics> => {
    const response = await api.get<ApiResponse<IndicatorStatistics>>(
      `/admin/statistics/questionnaires/${questionnaireId}/indicators/${indicatorId}`,
      { params: schoolId ? { schoolId } : {} }
    );
    return response.data.data;
  },

  /**
   * Get statistics by class (optionally filtered by school)
   */
  getStatisticsByClass: async (questionnaireId: number, indicatorId: number, schoolId?: number): Promise<ClassStatistics[]> => {
    const response = await api.get<ApiResponse<ClassStatistics[]>>(
      `/admin/statistics/questionnaires/${questionnaireId}/indicators/${indicatorId}/by-class`,
      { params: schoolId ? { schoolId } : {} }
    );
    return response.data.data;
  },

  /**
   * Get statistics by grade (optionally filtered by school)
   */
  getStatisticsByGrade: async (questionnaireId: number, indicatorId: number, schoolId?: number): Promise<GradeStatistics[]> => {
    const response = await api.get<ApiResponse<GradeStatistics[]>>(
      `/admin/statistics/questionnaires/${questionnaireId}/indicators/${indicatorId}/by-grade`,
      { params: schoolId ? { schoolId } : {} }
    );
    return response.data.data;
  },

  /**
   * Get schools that have been distributed a specific questionnaire
   */
  getQuestionnaireSchools: async (questionnaireId: number): Promise<SchoolOption[]> => {
    const response = await api.get<ApiResponse<SchoolOption[]>>(
      `/admin/statistics/questionnaires/${questionnaireId}/schools`
    );
    return response.data.data;
  },

  /**
   * Get users under a specific conclusion (drill-down)
   */
  getConclusionUsers: async (indicatorId: number, conclusionTitle: string): Promise<ConclusionUser[]> => {
    const response = await api.get<ApiResponse<ConclusionUser[]>>(
      `/admin/answers/by-conclusion`,
      { params: { indicatorId, conclusionTitle } }
    );
    return response.data.data;
  },

  /**
   * Get single answer detail
   */
  getAnswerDetail: async (answerId: number): Promise<QuestionnaireAnswer> => {
    const response = await api.get<ApiResponse<QuestionnaireAnswer>>(
      `/admin/answers/${answerId}`
    );
    return response.data.data;
  },

  /**
   * Get crisis alert users for a questionnaire
   */
  getCrisisAlertUsers: async (questionnaireId: number): Promise<ConclusionUser[]> => {
    const response = await api.get<ApiResponse<ConclusionUser[]>>(
      `/admin/answers/questionnaire/${questionnaireId}/crisis-alerts`
    );
    return response.data.data;
  },

  /**
   * 危机预警汇总：人数（distinct user）+ 次数（行总数）。
   * 同一学生在多个 crisis 指标都中招会算 N 次，但只算 1 人。
   */
  getCrisisAlertSummary: async (
    questionnaireId: number
  ): Promise<{ alertUsers: number; alertOccurrences: number }> => {
    const response = await api.get<
      ApiResponse<{ alertUsers: number; alertOccurrences: number }>
    >(`/admin/answers/questionnaire/${questionnaireId}/crisis-alerts/summary`);
    return response.data.data;
  },
};
